import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { fmtQty, fmtUnitTotals } from "@/lib/wmDigits";
import { wmMessage } from "@/lib/wmError";
import { WmBranchSelect } from "@/components/waste-meals/WmBranchSelect";
import { RecordPanel } from "@/components/waste-meals/RecordPanel";
import { WmExportButton } from "@/components/waste-meals/WmExportButton";
import { WASTE_PCT_STATE_KEY, fmt, fmtDay, fmtMoney, fmtPct, hasKey, readWastePct } from "@/components/waste-meals/wmUtils";

const G = OPS.wm.grid;

const MAIN_IDS = {
    table: G.table, row: G.row, cell: G.cell, total: G.total, cost: G.cost, missing: G.missingCost,
    dayTotal: G.dayTotal, beneficiaries: G.beneficiaries,
};
const RETURN_IDS = { table: G.returnsTable, row: G.returnsRow, cell: G.returnsCell, total: G.returnsTotal,
                     monthTotal: G.returnsMonthTotal };

function monthsBack(latest, n = 12) {
    let y = Number(latest.slice(0, 4));
    let m = Number(latest.slice(5, 7));
    const out = [];
    for (let i = 0; i < n; i++) {
        out.push(`${y}-${String(m).padStart(2, "0")}`);
        m -= 1;
        if (m < 1) { m = 12; y -= 1; }
    }
    return out;
}

/** The cost cell: SAR, or «—» when nothing in it carries a cost; «n بلا تكلفة»
 *  when some of its records were saved without one (never blocks a save). */
function CostCell({ value, missing, testId, missingTestId }) {
    const { t, lang } = useI18n();
    return (
        <td data-testid={testId}
            className="whitespace-nowrap px-3 py-2 text-center text-xs font-semibold tabular-nums text-[color:var(--ink)]">
            {value === null || value === undefined
                ? <span className="text-[color:var(--ink-faint)]">—</span>
                : <bdi dir="ltr">{fmtMoney(value, lang)}</bdi>}
            {missing > 0 ? (
                <span data-testid={missingTestId}
                      className="block text-[10px] font-semibold text-[color:var(--warning-soft-fg)]">
                    {fmt(t("wm_grid_missing_cost"), { n: fmtQty(missing, lang) })}
                </span>
            ) : null}
        </td>
    );
}

/**
 * One items × days matrix. Used twice: the month's waste/meals, and under it
 * the «مرتجع للمستودع» returns, which the first one (and every total and cost
 * in it) excludes. Totals come from the server, one value per unit, never mixed.
 */
function GridTable({ ids, rows, days, todayDay, todayRef, noActivity, beneficiaries, dayTotals, monthTotals,
                     showCost, monthCost, unitNames, onCell, footerLabel }) {
    const { t, lang } = useI18n();
    const dayList = useMemo(() => Array.from({ length: days }, (_, i) => i + 1), [days]);
    return (
        <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)]">
            <table data-testid={ids.table} className="w-max min-w-full border-collapse text-sm">
                <thead>
                    <tr className="bg-[color:var(--bg-surface-2)]">
                        <th className="sticky z-20 min-w-[9rem] max-w-[11rem] bg-[color:var(--bg-surface-2)] px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]"
                            style={{ insetInlineStart: 0 }}>
                            {t("wm_grid_item")}
                        </th>
                        {dayList.map((d) => (
                            <th key={d}
                                ref={todayRef && d === todayDay ? todayRef : null}
                                className={`px-2 py-2 text-center text-[11px] font-bold tabular-nums ${
                                    d === todayDay
                                        ? "bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-brown)]"
                                        : "text-[color:var(--ink-faint)]"
                                }`}>
                                <bdi dir="ltr">{d}</bdi>
                                {noActivity && noActivity.has(String(d)) ? (
                                    <span className="block text-[9px] font-normal opacity-70">
                                        {t("wm_no_activity_day")}
                                    </span>
                                ) : null}
                            </th>
                        ))}
                        <th className="px-3 py-2 text-center text-xs font-bold text-[color:var(--ink-muted)]">
                            {t("wm_grid_total")}
                        </th>
                        {showCost ? (
                            <th className="px-3 py-2 text-center text-xs font-bold text-[color:var(--ink-muted)]">
                                {t("wm_grid_cost")}
                            </th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.item_id} data-testid={ids.row(r.item_id)}
                            className="border-t border-[color:var(--separator)] hover:bg-[color:var(--brand-orange-soft)]/30">
                            <td className="sticky z-10 bg-[color:var(--bg-surface)] px-3 py-2 text-start"
                                style={{ insetInlineStart: 0 }}>
                                <span className="block truncate text-xs font-semibold text-[color:var(--ink)]">
                                    {lang === "ar" ? r.item_name_ar : r.item_name_en || r.item_name_ar}
                                </span>
                                <span className="text-[10px] text-[color:var(--ink-faint)]">
                                    {lang === "ar" ? r.unit_name_ar : r.unit_name_en}
                                    {r.kind === "stock" ? ` · ${t("wm_raw_badge")}` : ""}
                                    {r.is_active === false ? ` · ${t("wm_badge_inactive_item")}` : ""}
                                </span>
                            </td>
                            {dayList.map((d) => {
                                const v = r.cells[String(d)];
                                return (
                                    <td key={d} data-testid={ids.cell(r.item_id, d)}
                                        className={`px-2 py-2 text-center text-xs tabular-nums ${
                                            d === todayDay ? "bg-[color:var(--brand-orange-soft)]/40" : ""
                                        }`}>
                                        {v === undefined || v === null ? (
                                            <span className="text-[color:var(--ink-faint)]">·</span>
                                        ) : (
                                            <button type="button" onClick={() => onCell(r, d)}
                                                    className="font-semibold text-[color:var(--ink)] hover:underline">
                                                <bdi dir="ltr">{fmtQty(v, lang)}</bdi>
                                            </button>
                                        )}
                                    </td>
                                );
                            })}
                            <td data-testid={ids.total(r.item_id)}
                                className="px-3 py-2 text-center text-xs font-bold tabular-nums text-[color:var(--ink)]">
                                <bdi dir="ltr">{fmtQty(r.total, lang)}</bdi>
                            </td>
                            {showCost ? (
                                <CostCell value={r.cost_sar} missing={r.missing_cost_n || 0}
                                          testId={ids.cost ? ids.cost(r.item_id) : undefined}
                                          missingTestId={ids.missing ? ids.missing(r.item_id) : undefined} />
                            ) : null}
                        </tr>
                    ))}
                    {beneficiaries ? (
                        <tr className="border-t-2 border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)]">
                            <td className="sticky z-10 bg-[color:var(--bg-surface-2)] px-3 py-2 text-start text-xs font-bold text-[color:var(--ink)]"
                                style={{ insetInlineStart: 0 }}>
                                {t("wm_beneficiaries")}
                            </td>
                            {dayList.map((d) => (
                                <td key={d} data-testid={ids.beneficiaries ? ids.beneficiaries(d) : undefined}
                                    className="px-2 py-2 text-center text-xs tabular-nums text-[color:var(--ink-muted)]">
                                    {beneficiaries[String(d)] ?? <span className="text-[color:var(--ink-faint)]">·</span>}
                                </td>
                            ))}
                            <td className="px-3 py-2 text-center text-[10px] text-[color:var(--ink-faint)]">—</td>
                            {showCost ? <td /> : null}
                        </tr>
                    ) : null}
                    <tr className="border-t-2 border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)]">
                        <td className="sticky z-10 bg-[color:var(--bg-surface-2)] px-3 py-2 text-start text-xs font-bold text-[color:var(--ink)]"
                            style={{ insetInlineStart: 0 }}>
                            {footerLabel}
                        </td>
                        {dayList.map((d) => (
                            <td key={d} data-testid={ids.dayTotal ? ids.dayTotal(d) : undefined}
                                className="whitespace-nowrap px-2 py-2 text-center text-[10px] font-semibold tabular-nums text-[color:var(--ink-muted)]">
                                <bdi dir="ltr">
                                    {(dayTotals && fmtUnitTotals(dayTotals[String(d)], unitNames, lang)) || "·"}
                                </bdi>
                            </td>
                        ))}
                        <td data-testid={ids.monthTotal}
                            className="whitespace-nowrap px-3 py-2 text-center text-[10px] font-bold text-[color:var(--ink)]">
                            <bdi dir="ltr">{fmtUnitTotals(monthTotals, unitNames, lang)}</bdi>
                        </td>
                        {showCost ? (
                            <td className="whitespace-nowrap px-3 py-2 text-center text-[10px] font-bold text-[color:var(--ink)]">
                                {monthCost === null || monthCost === undefined
                                    ? <span className="text-[color:var(--ink-faint)]">—</span>
                                    : <bdi dir="ltr">{fmtMoney(monthCost, lang)}</bdi>}
                            </td>
                        ) : null}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

/**
 * The monthly grid: items down, days across. An empty cell is BLANK, never 0.
 * Day and month totals are one value per unit («12.4 كجم · 35 حبة»), never
 * mixed. Rows resolve the CURRENT item name; the record's name snapshot
 * serves the ledger only.
 *
 * 2026-10-16: «مرتجع للمستودع» is NOT waste (D2) — the server leaves it out
 * of every cell, total and cost here and returns it as `returns`, drawn as its
 * own table underneath. When the server sends cost (D5) the grid shows a cost
 * column, the month's cost and, for waste, the waste % of net sales («حتى …»
 * while the month's sales are still coming in). «تصدير Excel» (A3) sits in
 * the header.
 */
export function MonthlyGrid({ scope, type }) {
    const { t, lang } = useI18n();
    const [branchId, setBranchId] = useState(scope?.default_branch_id || "");
    const [month, setMonth] = useState((scope?.today || "").slice(0, 7));
    const [raw, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [panel, setPanel] = useState(null);
    const todayRef = useRef(null);
    const seq = useRef(0);
    // The answer for ANOTHER branch / month (a failed or still-running load
    // after a switch) is never drawn under the current selection.
    const data = useMemo(() => (raw
        && (!raw.branch_id || raw.branch_id === branchId)
        && (!raw.month || raw.month === month) ? raw : null), [raw, branchId, month]);

    const months = useMemo(() => monthsBack(scope?.today || "2026-01-01"), [scope]);
    const returnRows = useMemo(
        () => (type === "waste" ? data?.returns?.rows || [] : []), [data, type]);
    const unitNames = useMemo(() => {
        const m = {};
        for (const r of [...(data?.rows || []), ...returnRows]) {
            m[r.unit_key] = lang === "ar" ? r.unit_name_ar : r.unit_name_en;
        }
        return m;
    }, [data, returnRows, lang]);

    // Only the LATEST request may land: switching branch / month quickly must
    // never leave an older answer (its quantities, cost and waste %) on screen.
    const load = useCallback(async () => {
        if (!branchId || !month) return;
        const my = ++seq.current;
        setLoading(true);
        try {
            const res = await api.get("/waste-meals/grid", { params: { branch_id: branchId, month, type } });
            if (my === seq.current) setData(res.data);
        } catch (e) {
            if (my === seq.current) toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            if (my === seq.current) setLoading(false);
        }
    }, [branchId, month, type, lang, t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (todayRef.current) todayRef.current.scrollIntoView({ inline: "center", block: "nearest" });
    }, [data]);

    const rows = data?.rows || [];
    const days = data?.days || 30;
    const isThisMonth = month === (scope?.today || "").slice(0, 7);
    const todayDay = isThisMonth ? Number((scope?.today || "").slice(8, 10)) : -1;
    const noActivity = new Set(data?.no_activity_days || []);
    // Cost is serialised only for report viewers: an absent key = no column.
    const showCost = !!data && (hasKey(data, "month_cost_sar") || rows.some((r) => hasKey(r, "cost_sar")));
    const showReturnCost = returnRows.some((r) => hasKey(r, "cost_sar"));
    // the server's count when it sends one; else the rows' (a count, not a quantity)
    const missingTotal = hasKey(data, "missing_cost_n") ? Number(data.missing_cost_n) || 0
        : rows.reduce((a, r) => a + (Number(r.missing_cost_n) || 0), 0);
    const wp = readWastePct(data);
    const openPanel = (row, day) => setPanel({ row, day });

    return (
        <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] lg:flex lg:flex-wrap lg:items-center">
                <WmBranchSelect branches={scope?.branches} value={branchId} onChange={setBranchId} />
                <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger data-testid={G.month} className="h-9 lg:w-44">
                        <CalendarDays className="h-4 w-4 text-[color:var(--brand-brown)]" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {months.map((m) => (
                            <SelectItem key={m} value={m}>
                                <bdi dir="ltr">{m}</bdi>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <WmExportButton scope={scope} branchId={branchId} month={month} type={type} />
            </div>

            {data ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                    {data.scope === "own" ? (
                        <Badge data-testid={G.scopeBadge} variant="secondary" className="h-7 px-2.5 text-[11px]">
                            {t("wm_scope_own")}
                        </Badge>
                    ) : null}
                    <span data-testid={G.monthTotal} className="text-xs font-bold text-[color:var(--ink)]">
                        {t("wm_grid_month_total")}:{" "}
                        <bdi dir="ltr">{fmtUnitTotals(data.month_totals, unitNames, lang) || "—"}</bdi>
                    </span>
                    {showCost ? (
                        <span data-testid={G.monthCost} data-value={data.month_cost_sar ?? ""}
                              className="text-xs font-bold text-[color:var(--ink)]">
                            {t("wm_grid_month_cost")}:{" "}
                            {data.month_cost_sar === null || data.month_cost_sar === undefined
                                ? <span className="text-[color:var(--ink-faint)]">—</span>
                                : <bdi dir="ltr">{fmtMoney(data.month_cost_sar, lang)}</bdi>}
                        </span>
                    ) : null}
                    {type === "waste" && wp.present ? (
                        <span data-testid={G.wastePct} data-value={wp.pct ?? ""} data-state={wp.state || ""}
                              className="text-xs font-bold text-[color:var(--ink)]">
                            {t("wm_grid_waste_pct")}:{" "}
                            {wp.pct === null
                                ? <span className="font-semibold text-[color:var(--ink-faint)]">
                                    {t(WASTE_PCT_STATE_KEY[wp.state] || "wm_grid_waste_pct_none")}
                                  </span>
                                : <bdi dir="ltr" className="text-[color:var(--brand-brown)]">{fmtPct(wp.pct, lang)}</bdi>}
                            {wp.pct !== null && wp.through ? (
                                <span className="ms-1 text-[10px] font-semibold text-[color:var(--ink-faint)]">
                                    ({t("wm_grid_through")} <bdi>{fmtDay(String(wp.through).slice(0, 10), lang)}</bdi>)
                                </span>
                            ) : null}
                        </span>
                    ) : null}
                    {showCost && missingTotal > 0 ? (
                        <span className="text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                            {fmt(t("wm_grid_missing_cost_total"), { n: fmtQty(missingTotal, lang) })}
                        </span>
                    ) : null}
                </div>
            ) : null}

            {loading ? <InlineLoader /> : !rows.length ? (
                <p data-testid={G.empty}
                   className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                    {t("wm_grid_empty")}
                </p>
            ) : (
                <GridTable ids={MAIN_IDS} rows={rows} days={days} todayDay={todayDay} todayRef={todayRef}
                           noActivity={noActivity}
                           beneficiaries={type === "meals" ? data.beneficiaries : null}
                           dayTotals={data.day_totals} monthTotals={data.month_totals}
                           showCost={showCost} monthCost={data.month_cost_sar}
                           unitNames={unitNames} onCell={openPanel} footerLabel={t("wm_grid_day_total")} />
            )}

            {!loading && returnRows.length ? (
                <section className="space-y-2 pt-2">
                    <header className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <Undo2 className="h-4 w-4 text-[color:var(--brand-brown)]" />
                        <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("wm_returns_title")}</h3>
                        <span className="text-[11px] text-[color:var(--ink-faint)]">{t("wm_returns_hint")}</span>
                    </header>
                    <GridTable ids={RETURN_IDS} rows={returnRows} days={days} todayDay={todayDay}
                               dayTotals={data?.returns?.day_totals} monthTotals={data?.returns?.month_totals}
                               showCost={showReturnCost} monthCost={data?.returns?.month_cost_sar}
                               unitNames={unitNames} onCell={openPanel} footerLabel={t("wm_returns_month_total")} />
                </section>
            ) : null}

            <RecordPanel open={!!panel} onOpenChange={(v) => !v && setPanel(null)}
                         branchId={branchId} type={type}
                         date={panel ? `${month}-${String(panel.day).padStart(2, "0")}` : null}
                         itemId={panel?.row?.item_id}
                         itemName={panel ? (lang === "ar" ? panel.row.item_name_ar : panel.row.item_name_en) : ""}
                         onChanged={load} />
        </div>
    );
}
